<script setup>
import { ref } from "vue";
import VehicleAvatar from "@/components/VehicleAvatar.vue";

const props = defineProps({
  vehicles: { type: Array, default: () => [] },
  plans: { type: Array, default: () => [] },
  visibleRouteKeys: { type: Array, default: () => [] },
});

const emit = defineEmits([
  "toggle-plan",
  "toggle-route",
  "toggle-vehicle",
]);

const panelOpen = ref(true);
const vehiclesSectionOpen = ref(true);
const routesSectionOpen = ref(false);


function isRouteVisible(route) {
  return props.visibleRouteKeys.includes(String(route?.key || ""));
}

</script>

<template>
  <aside class="live-panel" :class="{ 'is-collapsed': !panelOpen }">
    <!-- Header -->
    <div class="live-panel__header">
      <div class="live-panel__live-badge">
        <span class="live-panel__live-dot" aria-hidden="true"></span>
        <span class="live-panel__live-label">Live</span>
      </div>
      <button
        type="button"
        class="live-panel__collapse-btn"
        :aria-label="panelOpen ? 'Colapsar panel' : 'Expandir panel'"
        @click="panelOpen = !panelOpen"
      >
        <i class="fa" :class="panelOpen ? 'fa-chevron-up' : 'fa-chevron-down'" aria-hidden="true"></i>
      </button>
    </div>

    <template v-if="panelOpen">
    <!-- Vehicles section -->
    <div class="live-panel__section">
      <button
        type="button"
        class="live-panel__section-toggle"
        :aria-expanded="vehiclesSectionOpen"
        @click="vehiclesSectionOpen = !vehiclesSectionOpen"
      >
        <span class="live-panel__section-title">Vehículos</span>
        <i
          class="fa live-panel__section-chevron"
          :class="vehiclesSectionOpen ? 'fa-chevron-up' : 'fa-chevron-down'"
          aria-hidden="true"
        ></i>
      </button>

      <div v-if="vehiclesSectionOpen" class="live-panel__section-body">
        <div v-if="vehicles.length === 0" class="live-panel__empty">
          Sin vehículos
        </div>
        <button
          v-for="vehicle in vehicles"
          :key="vehicle.id"
          type="button"
          class="live-vehicle-row"
          :class="{ 'is-hidden': vehicle.visible === false, 'is-active': vehicle.visible !== false }"
          @click="emit('toggle-vehicle', vehicle.id)"
        >
          <vehicle-avatar
            :label="vehicle.name"
            :abbreviation="vehicle.abbreviation"
            :accent-color="vehicle.color"
            :size="26"
          />
          <span class="live-row__content">
            <span class="live-row__title">{{ vehicle.name }}</span>
          </span>
          <span
            class="live-row__eye"
            :class="{ 'is-active': vehicle.visible !== false }"
            aria-hidden="true"
          >
            <i
              class="fa-regular"
              :class="vehicle.visible !== false ? 'fa-eye' : 'fa-eye-slash'"
            ></i>
          </span>
        </button>
      </div>
    </div>

    <div class="live-panel__sep" aria-hidden="true"></div>

    <!-- Routes section -->
    <div class="live-panel__section">
      <button
        type="button"
        class="live-panel__section-toggle"
        :aria-expanded="routesSectionOpen"
        @click="routesSectionOpen = !routesSectionOpen"
      >
        <span class="live-panel__section-title">Rutas</span>
        <i
          class="fa live-panel__section-chevron"
          :class="routesSectionOpen ? 'fa-chevron-up' : 'fa-chevron-down'"
          aria-hidden="true"
        ></i>
      </button>

      <div v-if="routesSectionOpen" class="live-panel__section-body live-panel__section-body--routes">
        <div v-if="plans.length === 0" class="live-panel__empty">
          Sin rutas para esta fecha.
        </div>
        <div v-for="plan in plans" :key="plan.id" class="live-plan-group">
          <button
            type="button"
            class="live-plan-group__toggle"
            @click="emit('toggle-plan', plan.id)"
          >
            <i
              class="fa live-plan-group__chevron"
              :class="plan.isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"
              aria-hidden="true"
            ></i>
            <span class="live-plan-group__name">{{ plan.name }}</span>
          </button>

          <div v-if="plan.isExpanded" class="live-plan-group__body">
            <div v-if="plan.routesLoading" class="live-panel__status">
              Cargando rutas...
            </div>
            <div v-else-if="plan.routesError" class="live-panel__status is-error">
              {{ plan.routesError }}
            </div>
            <div v-else-if="!plan.routes.length" class="live-panel__status">
              Sin rutas.
            </div>
            <button
              v-for="route in plan.routes.filter(r => r.stopCount > 0)"
              v-else
              :key="route.key"
              type="button"
              class="live-route-row"
              :class="{ 'is-active': isRouteVisible(route) }"
              @click="emit('toggle-route', route)"
            >
              <vehicle-avatar
                :abbreviation="route.route?.abbreviation || route.name"
                :accent-color="route.color"
                :size="26"
              />
              <span class="live-row__content">
                <span class="live-row__title">{{ route.name }}</span>
              </span>
              <span
                class="live-row__eye"
                :class="{ 'is-active': isRouteVisible(route) }"
                aria-hidden="true"
              >
                <i
                  class="fa-regular"
                  :class="isRouteVisible(route) ? 'fa-eye' : 'fa-eye-slash'"
                ></i>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
    </template>
  </aside>
</template>

<style scoped lang="scss">
@use "./live-panels-shared" as shared;

.live-panel {
  @include shared.live-panel-shell(min(294px, calc(100vw - 32px)));
  gap: 0;
}

.live-panel.is-collapsed {
  @include shared.live-panel-collapsed;
}

.live-panel__header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}

.live-panel.is-collapsed .live-panel__header {
  margin-bottom: 0;
}

.live-panel__live-badge {
  align-items: center;
  display: inline-flex;
  gap: 6px;
}

.live-panel__collapse-btn {
  @include shared.live-panel-collapse;
  font-size: 0.66rem;
  height: 22px;
  width: 22px;
}

.live-panel__live-dot {
  animation: live-dot-pulse 2.2s ease-in-out infinite;
  background: #22c55e;
  border-radius: 999px;
  box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.2);
  display: inline-block;
  height: 7px;
  width: 7px;
}

@keyframes live-dot-pulse {
  0%, 100% { opacity: 1; box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.2); }
  50% { opacity: 0.55; box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.08); }
}

.live-panel__live-label {
  color: #0f172a;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.01em;
}


.live-panel__sep {
  background: rgba(226, 232, 240, 0.8);
  height: 1px;
  margin: 6px 0;
}

.live-panel__section {
  display: flex;
  flex-direction: column;
}

.live-panel__section-toggle {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 8px;
  color: #0f172a;
  display: flex;
  gap: 6px;
  padding: 5px 3px;
  text-align: left;
  transition: background 0.14s ease;
  width: 100%;
}

.live-panel__section-toggle:hover {
  background: rgba(241, 245, 249, 0.9);
}

.live-panel__section-title {
  color: #0f172a;
  flex: 1 1 auto;
  font-size: 0.76rem;
  font-weight: 600;
}

.live-panel__section-badge {
  @include shared.live-panel-counter(
    rgba(94, 114, 228, 0.07),
    rgba(94, 114, 228, 0.14),
    var(--bs-primary, #5e72e4)
  );
}

.live-panel__section-chevron {
  color: #94a3b8;
  flex: 0 0 auto;
  font-size: 0.54rem;
}

.live-panel__section-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 2px;
  margin-top: 4px;
  max-height: min(36dvh, 260px);
  overflow-y: auto;
}

.live-panel__section-body--routes {
  max-height: min(42dvh, 300px);
}


.live-panel__empty,
.live-panel__status {
  color: var(--color-text-secondary);
  font-size: 0.67rem;
  padding: 4px 4px;
  text-align: center;
}

.live-panel__status {
  text-align: left;
}

.live-panel__status.is-error {
  color: #b42318;
}

.live-vehicle-row,
.live-route-row {
  @include shared.live-row-card(auto minmax(0, 1fr) auto, 5px 7px);
  gap: 7px;
}

.live-vehicle-row:hover,
.live-route-row:hover {
  @include shared.live-row-card-hover;
}

.live-vehicle-row.is-hidden {
  opacity: 0.5;
}


.live-row__content {
  @include shared.live-row-content;
}

.live-row__title {
  @include shared.live-row-title(0.71rem);
}

.live-row__meta {
  @include shared.live-row-meta(0.59rem);
}

.live-row__eye {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: #cbd5e1;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.78rem;
  height: 20px;
  justify-content: center;
  padding: 0;
  transition: color 0.14s ease;
  width: 20px;
}

.live-vehicle-row.is-active .live-row__eye,
.live-route-row.is-active .live-row__eye,
.live-row__eye.is-active {
  color: var(--bs-primary, #5e72e4);
}

.live-vehicle-row.is-hidden .live-row__eye,
.live-route-row:not(.is-active) .live-row__eye {
  color: #cbd5e1;
}

.live-plan-group {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.live-plan-group__toggle {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 8px;
  color: var(--color-text-primary);
  display: flex;
  gap: 6px;
  padding: 3px 3px;
  text-align: left;
  transition: background 0.14s ease;
  width: 100%;
}

.live-plan-group__toggle:hover {
  background: rgba(241, 245, 249, 0.9);
}

.live-plan-group__chevron {
  color: #94a3b8;
  flex: 0 0 auto;
  font-size: 0.5rem;
}

.live-plan-group__name {
  flex: 1 1 auto;
  font-size: 0.69rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.live-plan-group__count {
  background: #f1f5f9;
  border-radius: 999px;
  color: var(--color-text-secondary);
  flex: 0 0 auto;
  font-size: 0.58rem;
  font-weight: 600;
  padding: 2px 6px;
}

.live-plan-group__body {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 6px;
}
</style>
